import type React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Typography from './typography';
import type { Banner } from 'app/types/graphql';

type Props = {
  banner: Banner;
  isSingle: boolean;
  onClick: () => void;
};

const BannerContainer = styled.div<{ imageUrl: string; mobileImageUrl?: string; $isSingle: boolean }>`
  width: ${({ $isSingle }) => ($isSingle ? '100%' : '75vw')};
  flex-shrink: 0;
  aspect-ratio: 6/3;
  background-image: url(${({ mobileImageUrl, imageUrl }) => mobileImageUrl || imageUrl});
  background-size: cover;
  background-position: right;
  position: relative;
  padding: ${({ theme }) => theme.spacing(6)};
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.blackSwan};
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
  }

  ${({ theme: { media }, imageUrl, $isSingle }) => `
    ${media.medium(
      () => `
        width: ${$isSingle ? '100%' : '85vw'};
        aspect-ratio: 10/2;
        background-image: url(${imageUrl});
        background-position: center;

        &::after {
          content: none;
        }
      `,
    )}
  `}

  ${({ theme: { media }, $isSingle }) => `
    ${media.large(
      () => `
        width: ${$isSingle ? '100%' : '90%'};
      `,
    )}
  `}

  ${({ theme: { media }, $isSingle }) => `
    ${media.xl(
      () => `
        width: ${$isSingle ? '100%' : '65vw'};
      `,
    )}
  `}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const BannerTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-size: ${({ theme }) => theme.typography.small};
  text-transform: uppercase;
  
  ${({ theme: { typography, media } }) => `
    ${media.medium(
      () => `
        font-size: ${typography.xxlarge};
      `,
    )}
  `}
`;

const BannerDescription = styled(Typography)`
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-size: ${({ theme }) => theme.typography.tiny};
  
  ${({ theme: { typography, media } }) => `
    ${media.medium(
      () => `
        font-size: ${typography.small};
      `,
    )}
  `}
`;

const DynamicBanner: React.FC<Props> = ({ banner, isSingle, onClick }) => {
  const imageUrl = banner.image.signedUrl;
  const mobileImageUrl = banner.mobileImage?.signedUrl;

  return (
    <BannerContainer imageUrl={imageUrl} mobileImageUrl={mobileImageUrl} $isSingle={isSingle} onClick={onClick}>
      <ContentWrapper>
        <BannerTitle variant="h2">{banner.title}</BannerTitle>
        <BannerDescription htmlContent={banner.description} variant="caption" />
      </ContentWrapper>
    </BannerContainer>
  );
};

export default DynamicBanner;
